import { Alert } from '@tempmee/react';
import { EducationUtils } from '~/utils/EducationUtils';
import { resolveSpecialtyAlert, TSpecialtyAlertKind } from '../utils/shiftAlerts';

export interface ISpecialtyMismatchAlertProps {
  /** The shift's preferred specialty, named in the mismatch copy. */
  jobSpecialty?: string;
  /** The candidate holds the shift's preferred specialty. */
  specialtyMatch: boolean;
  /** The candidate shares no insurance provider with the office. Dentists only. */
  insuranceMismatch: boolean;
  /** Dentist shifts word the mismatch in terms of procedures rather than a named specialty. */
  isDentistShift: boolean;
}

/**
 * The qualification warning that sits under the SPECIALTIES heading, inside the
 * `CandidateDetails` block's own section via its `specialtiesAlert` slot.
 *
 * Only the copy lives here. Which specialties show, the heading above them and
 * the chips below are the block's again — this used to re-draw that whole
 * section locally because the block had no slot an alert could occupy.
 *
 * Renders `null` when there is nothing to warn about, so the caller can pass it
 * unconditionally and the block draws its section unchanged.
 */
export const SpecialtyMismatchAlert = ({
  jobSpecialty,
  specialtyMatch,
  insuranceMismatch,
  isDentistShift,
}: ISpecialtyMismatchAlertProps) => {
  const kind = resolveSpecialtyAlert({ specialtyMatch, insuranceMismatch });

  if (kind === null) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      description={describeMismatch(kind, { isDentistShift, jobSpecialty })}
      data-testid="missing-specialty-alert"
      data-cy="missing-specialty-alert"
    />
  );
};

/**
 * Mismatch copy.
 *
 * The named-specialty wording is the design's; the legacy banner said "your
 * preferred specialty, X" and the redesign frame says "your desired specialty
 * listed: X". The dentist and insurance wordings stay as the legacy banner had
 * them, the design covering only the named-specialty case — a dentist shift's
 * requirement is a procedure set rather than a single named specialty.
 */
const describeMismatch = (
  kind: TSpecialtyAlertKind,
  { isDentistShift, jobSpecialty }: { isDentistShift: boolean; jobSpecialty?: string },
) => {
  if (isDentistShift) {
    if (kind === 'both') {
      return 'This professional does not have experience in all procedures needed or the same Insurance Provider.';
    }

    return kind === 'specialty'
      ? 'This professional does not have experience in all procedures needed.'
      : 'This professional does not have the same Insurance Provider.';
  }

  return (
    <>
      This professional does not have your desired specialty listed:{' '}
      <strong>{EducationUtils.removeSpecialtyPrefix(jobSpecialty)}</strong>.
    </>
  );
};
